import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Papa from 'papaparse';
import { translateIndexedName } from '../../shared/helpers/indexed-name.helper';
import { Project } from '../objects/project';
import { ProjectInfo } from '../types';
import { CargoSpacesSummaryService } from './cargo-spaces-summary.service';
import { ProjectService } from './project.service';

@Injectable({
  providedIn: 'root',
})
export class ExportToCsvService {
  private project: Project;

  constructor(
    private cargoSpacesSummary: CargoSpacesSummaryService,
    private projectService: ProjectService,
    private translate: TranslateService,
    private datePipe: DatePipe,
  ) {
    this.projectService.project.subscribe((project) => {
      this.project = project;
    });
  }

  public createCsv(project: ProjectInfo, separator: 'comma' | 'semicolon') {
    const rows = [];
    this.project.workSpaces.forEach((workspace, i) => {
      const summary = this.cargoSpacesSummary.calculateCargoSpacesSummary(i);
      const title = [
        'No.',
        this.translate.instant('loading_type'),
        this.translate.instant('loading_name'),
        this.translate.instant('load_name'),
        this.translate.instant('length'),
        this.translate.instant('width'),
        this.translate.instant('diameter'),
        this.translate.instant('height'),
        this.translate.instant('load_quantity'),
        this.translate.instant('load_weight'),
        this.translate.instant('stack'),
        this.translate.instant('stack_quantity'),
        this.translate.instant('sum_of_loads_weight'),
        this.translate.instant('ldm_free'),
        this.translate.instant('ldm_taken'),
        this.translate.instant('loaded_loads'),
        this.translate.instant('loads_volume'),
        this.translate.instant('free_space'),
      ];
      rows.push(title);

      workspace.cargoSpaces.forEach((cargospace, index) => {
        const cargoSpacesWithSameName = this.spacesWithSameName(cargospace.name);
        const cargoSpacesBeforeThis = cargoSpacesWithSameName.filter(
          (work) => work.id <= workspace.id,
        );
        const translatedName = translateIndexedName(cargospace.name, (key) =>
          this.translate.instant(key) as string,
        );
        const name =
          cargoSpacesWithSameName.length > 1
            ? `${translatedName} ${cargoSpacesBeforeThis.length}/${cargoSpacesWithSameName.length}`
            : translatedName;
        const cargoRow = [
          index + 1,
          cargospace.type,
          name,
          undefined,
          cargospace.dimensions.length,
          cargospace.dimensions.width,
          cargospace.dimensions.diameter,
          cargospace.dimensions.height,
          undefined,
          undefined,
          undefined,
          undefined,
          summary[index].totalWeight,
          summary[index].freeLdm,
          summary[index].takenLdm,
          summary[index].loadedLoads,
          summary[index].occupiedVolume,
          summary[index].freeArea,
        ];
        rows.push(cargoRow);
        cargospace.groups.forEach((group) => {
          const groupRow = [
            index + 1,
            undefined,
            undefined,
            this.translate.instant(group.name),
            group.dimensions.length,
            group.dimensions.width,
            group.dimensions.diameter,
            group.dimensions.height,
            group.quantity,
            group.weight,
            group.stacking ? 'Y' : 'N',
            group.stackingOptions ? group.stackingOptions.maxLoadsOnTop : '',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
          ];
          rows.push(groupRow);
        });
      });

      rows.push([
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
      ]);
    });

    const blob = new Blob(
      ['\uFEFF', Papa.unparse(rows, { delimiter: separator === 'comma' ? ',' : ';' })],
      {
        type: 'text/csv;charset=utf-8',
      },
    );
    const url = window.URL.createObjectURL(blob);
    let fileName: string;

    if (project && project.name) {
      fileName = `${project.name}.csv`;
    } else {
      fileName = `GL${this.datePipe.transform(Date.now(), 'yyMMddHHmm')}.csv`;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  private spacesWithSameName(cargoSpaceName: string) {
    return this.project.workSpaces.filter(
      (workspace) =>
        translateIndexedName(workspace.cargoSpaces[0].name, (key) =>
          this.translate.instant(key) as string,
        ) ===
          translateIndexedName(cargoSpaceName, (key) => this.translate.instant(key) as string),
    );
  }
}
